import { Color } from 'three/webgpu';
import { exponentialHeightFogFactor, fog, renderGroup, uniform } from 'three/tsl';

/**
 * ── ⭐ F1 · THE HEIGHT FOG NODE — THREE'S OWN NODES, COMPOSED, NOT NOVEL TSL ────────────────────
 *
 * CLAUDE.md hard rule 3: *TSL snippets come only from `docs/ai/tsl-patterns.ts`* and *prefer
 * composing built-in node materials over authoring novel TSL*. This file is the second clause,
 * exactly: every operator below is three's own, from `three/src/nodes/fog/Fog.js`, exported from
 * the `three/tsl` runtime build (verified in `three.tsl.js`'s export list, three 0.185.1). There is
 * NO new maths here at all — the graph is
 *
 *     fog( colorUniform, exponentialHeightFogFactor( densityUniform, topUniform ) )
 *
 * which is the same SHAPE three itself builds for `scene.fog` in `NodeManager.updateFog`:
 *
 *     fog( reference('color', …), rangeFogFactor( reference('near', …), reference('far', …) ) )
 *
 * — references instead of uniforms, and every one of them `.setGroup( renderGroup )`, which is
 * where the discipline below comes from rather than from our own taste.
 *
 * ── THE SEAM: `scene.fogNode` ───────────────────────────────────────────────────────────────────
 *
 * `NodeManager.getFogNode( scene )` returns `scene.fogNode || this.get(scene).fogNode || null`
 * (r185, `NodeManager.js:576`), and `NodeMaterial.setupOutput` folds it into every material whose
 * `material.fog === true`. So an explicit `scene.fogNode` WINS over the node three derives from
 * `scene.fog`, and `sky.ts`'s `mat.fog = false` opt-out survives untouched (its own TRAP 2 note).
 *
 * That is a first-party seam on BOTH backends, which is why the height fog is not a post-processing
 * pass: `pipeline.ts` exists only for non-bilinear upscale modes, every shipped tier is `bilinear`,
 * and a fog pass would have had to bring that whole chain into existence at a measured 4.5–6.9 ms
 * per 1.44 Mpix frame. This costs no pass at all.
 *
 * ── ⚠️ THE CACHE KEY IS THE SHARP EDGE, AND IT IS THE FOG ANALOGUE OF `LightsNode` ─────────────
 *
 * `NodeManager.getCacheKey` pushes `fogNode.getCacheKey()` into the per-call SCENE key
 * (`NodeManager.js:616-617`), so SWAPPING the node — null ⇄ this node — regenerates every visible
 * material's WGSL, exactly like a light `castShadow` write does. Therefore:
 *
 *   · the node is built ONCE and kept (`createHeightFog` is called lazily, once per scene);
 *   · colour / density / top are UNIFORM WRITES on that stable node and are FREE;
 *   · only an authored `mode` change (or fog on/off) swaps it, and `scene.applyFog` counts those
 *     into `__fwg.state().fog.keyCycles` so "the editor hitches when I touch fog" is a number
 *     somebody can read rather than a feeling.
 *
 * ── ⚠️ PATTERN 5 IS LIVE HERE ───────────────────────────────────────────────────────────────────
 *
 * A bare `uniform(x)` lands in `objectGroup` — the per-render-object buffer — and MEASURED on this
 * repo's probe gain it keeps ~92 % of a value you have just changed. For a feature whose entire
 * purpose is dialling a look in, a slider that moves the number and not the picture is the exact
 * wrong outcome. Every uniform below is `.setGroup( renderGroup )`, and
 * `tests/renderFogNode.test.ts` asserts the `render.` struct prefix in the emitted code rather than
 * trusting this comment.
 */

export interface HeightFogHandle {
  /** The node to assign to `scene.fogNode`. Built once; never rebuilt for a value change. */
  readonly node: ReturnType<typeof fog>;
  /** Uniform writes — free, no pipeline move. `color` is an sRGB hex string, as the schema stores. */
  set(next: { color: string; density: number; topM: number }): void;
  /** What the uniforms currently hold — read back for `__fwg.state().fog`, never re-derived. */
  values(): { color: string; density: number; topM: number };
}

export function createHeightFog(initial: {
  color: string;
  density: number;
  topM: number;
}): HeightFogHandle {
  /*
   * `Color` decodes the hex from sRGB into the renderer's working space, which is what
   * `THREE.Fog` does with its own colour — so switching modes keeps the same authored `#1a2836`
   * looking like the same grey. A raw `vec3` of the hex bytes would read visibly lighter.
   */
  const colorU = uniform(new Color(initial.color)).setGroup(renderGroup);
  const densityU = uniform(initial.density).setGroup(renderGroup);
  const topU = uniform(initial.topM).setGroup(renderGroup);
  let current = { ...initial };

  const node = fog(colorU, exponentialHeightFogFactor(densityU, topU));

  return {
    node,
    set(next) {
      // `Color.set` re-decodes from sRGB in place: no new object, no node touched, no key move.
      (colorU.value as Color).set(next.color);
      densityU.value = next.density;
      topU.value = next.topM;
      current = { ...next };
    },
    values() {
      return { ...current };
    },
  };
}
